import { BaseViewModel } from "@iot-platform/core";
import type { ReportRouteGpsFilter } from "@report/domain/models/report-route-gps.ts";
import { ReportGetRouteGpsUseCase } from "@report/domain/usecases/report-get-route-gps.usecase.ts";
import { StationTypeEnumSchema } from "@station/data/dto/station.dto.ts";
import type { Station } from "@station/domain/models/station.model.ts";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

const defaultToKm = 1000;

@injectable()
export class HistoricalFilterViewmodel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    from: DateTime = DateTime.now().minus({ days: 1 });

    @observable
    to: DateTime = DateTime.now();

    @observable
    errors: { from?: string; to?: string } = {};

    @observable
    currentStation: Readonly<Station> | null = null;

    @observable
    roads: string[] = [];

    @observable
    selectedRoad: string | null = null;

    @observable
    fromKm?: number = 0;

    @observable
    toKm?: number = defaultToKm;

    constructor(
        @inject(ReportGetRouteGpsUseCase)
        private readonly reportGetRouteGpsUseCase: ReportGetRouteGpsUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await this.fetchRouteGps();

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async fetchRouteGps(): Promise<void> {
        if (!this.currentStation) throw new Error("Current station is not set");

        if (this.currentStation.type === StationTypeEnumSchema.enum.Gps) {
            const reportRouteGpsFilter: ReportRouteGpsFilter = {
                from: this.from.toSeconds(),
                to: this.to.toSeconds(),
                stationId: Number(this.currentStation.id.value),
            };
            const routeGps =
                await this.reportGetRouteGpsUseCase.execute(
                    reportRouteGpsFilter,
                );

            this.roads = routeGps.map((route) => route.roads).flat();
        }
    }

    @action
    setStation = (station: Readonly<Station>): void => {
        runInAction(() => {
            this.currentStation = station;
        });
    };

    @action
    setSelectedRoad = (road: string | null): void => {
        if (road === null) {
            this.setFromKm(0);
            this.setToKm(defaultToKm);
        }
        runInAction(() => {
            this.selectedRoad = road;
        });
    };

    @action
    setFromKm = (fromKm: number): void => {
        runInAction(() => {
            this.fromKm = fromKm;
        });
    };

    @action
    setToKm = (toKm: number): void => {
        runInAction(() => {
            this.toKm = toKm;
        });
    };
}
